import { useCallback, useRef } from 'react'
import { useRouterContext } from 'components/RouterContext'

const VALID_PAGE_SIZES = new Set([10, 20, 50, 100])
const DEFAULT_PAGE_SIZE = 20
const DEFAULT_SORT_BY = 'updatedAt'
const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc'

export interface OverrideQueryParams {
  search: string
  state: string | undefined
  issuerId: string | undefined
  hiosPlanId: string | undefined
  ruleId: string | null
  mode: 'view' | 'edit'
  page: number
  pageSize: number
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

export function useOverrideQueryParams() {
  const { router, location } = useRouterContext()

  // Always use the latest location inside callbacks to avoid stale closure issues
  // (e.g. pageSize changes and search changes both need the up-to-date query).
  const locationRef = useRef(location)
  locationRef.current = location

  const urlQuery = (location?.query ?? {}) as Record<string, string>

  const rawPageSize = parseInt(urlQuery.pageSize, 10)
  const parsedPageSize = VALID_PAGE_SIZES.has(rawPageSize)
    ? rawPageSize
    : DEFAULT_PAGE_SIZE

  const rawPage = parseInt(urlQuery.page, 10)
  const parsedPage = rawPage >= 1 ? rawPage : 1

  const parsed: OverrideQueryParams = {
    search: urlQuery.search ?? '',
    state: urlQuery.state || undefined,
    issuerId: urlQuery.issuerId || undefined,
    hiosPlanId: urlQuery.hiosPlanId || undefined,
    ruleId: urlQuery.ruleId ?? null,
    mode: urlQuery.mode === 'edit' ? 'edit' : 'view',
    page: parsedPage,
    pageSize: parsedPageSize,
    sortBy: urlQuery.sortBy ?? DEFAULT_SORT_BY,
    sortOrder: urlQuery.sortOrder === 'asc' ? 'asc' : DEFAULT_SORT_ORDER,
  }

  // Merges partial into the current URL query via router.replace.
  // Keys set to '' are removed from the URL.
  // Restores scrollY because the app-wide Router onUpdate scrolls to top on every URL change,
  // which is wrong for in-page actions like pagination, sort, search, and modal open/close.
  const updateQuery = useCallback(
    (partial: Record<string, string>) => {
      const current = (locationRef.current?.query ?? {}) as Record<
        string,
        string
      >
      const merged: Record<string, string> = { ...current, ...partial }
      for (const key of Object.keys(partial)) {
        if (partial[key] === '') delete merged[key]
      }
      const savedY = window.scrollY
      router.replace({ ...locationRef.current, query: merged })
      requestAnimationFrame(() => window.scrollTo({ top: savedY }))
    },
    [router]
  )

  // Pushes a new subtab path.
  // By default preserves search/pageSize/sort and clears ruleId/mode (modal state).
  // Pass clearModal=false to also preserve ruleId/mode.
  const navigateSubtab = useCallback(
    (path: string, clearModal = true) => {
      const current = (locationRef.current?.query ?? {}) as Record<
        string,
        string
      >
      const next: Record<string, string> = {}
      if (current.search) next.search = current.search
      if (current.state) next.state = current.state
      if (current.issuerId) next.issuerId = current.issuerId
      if (current.hiosPlanId) next.hiosPlanId = current.hiosPlanId
      if (current.pageSize) next.pageSize = current.pageSize
      if (current.sortBy) next.sortBy = current.sortBy
      if (current.sortOrder) next.sortOrder = current.sortOrder
      if (!clearModal) {
        if (current.ruleId) next.ruleId = current.ruleId
        if (current.mode) next.mode = current.mode
      }
      router.push({ pathname: path, query: next })
    },
    [router]
  )

  const handlePageChange = useCallback(
    (page: number) => {
      updateQuery({ page: String(page) })
    },
    [updateQuery]
  )

  const handlePageSizeChange = useCallback(
    (size: number) => {
      updateQuery({ pageSize: String(size), page: '1' })
    },
    [updateQuery]
  )

  const handleSortChange = useCallback(
    (colId: string, sort: 'asc' | 'desc') => {
      updateQuery({ sortBy: colId, sortOrder: sort, page: '1' })
    },
    [updateQuery]
  )

  return {
    parsed,
    updateQuery,
    navigateSubtab,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
  }
}
